/**
 * My Data Page
 *
 * User data management page with storage usage, file upload, and file browser.
 */

import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StorageUsage, FileUpload, FileBrowser } from '@/components/files';
import { useStorage } from '@/lib/hooks/useStorage';
import type { UserFileResponse } from '@/lib/api';

export function MyDataPage() {
  const [searchParams] = useSearchParams();
  const initialFolderId = searchParams.get('folderId') ?? undefined;
  const { refresh: refreshStorage } = useStorage({ autoFetch: true });
  const [activeRefreshTrigger, setActiveRefreshTrigger] = useState(0);
  const [archivedRefreshTrigger, setArchivedRefreshTrigger] = useState(0);

  const handleUploadComplete = useCallback(
    (files: UserFileResponse[]) => {
      // Refresh both storage and active file list after upload
      refreshStorage();
      setActiveRefreshTrigger((prev) => prev + 1);
      console.log(`Uploaded ${files.length} file(s)`);
    },
    [refreshStorage],
  );

  const handleFilesDeleted = useCallback(() => {
    // Refresh storage after deletion
    refreshStorage();
  }, [refreshStorage]);

  const handleFileArchived = useCallback(() => {
    // Refresh both tabs when a file is archived
    refreshStorage();
    setActiveRefreshTrigger((prev) => prev + 1);
    setArchivedRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  const handleFileRestored = useCallback(() => {
    // Refresh both tabs when a file is restored
    refreshStorage();
    setActiveRefreshTrigger((prev) => prev + 1);
    setArchivedRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  return (
    <div>
      {/* Page Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-foreground">My Data</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your files and storage</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-6">
        {/* Storage Usage */}
        <StorageUsage showBreakdown={true} />

        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Files</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              onUploadComplete={handleUploadComplete}
              maxFiles={10}
              maxSize={500 * 1024 * 1024}
            />
          </CardContent>
        </Card>

        {/* File Browser with Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="active">
              <TabsList>
                <TabsTrigger value="active">Active Files</TabsTrigger>
                <TabsTrigger value="archived">Archived</TabsTrigger>
              </TabsList>
              <TabsContent value="active" forceMount className="data-[state=inactive]:hidden">
                <p className="text-sm text-muted-foreground mb-4">
                  Files available for immediate access. Download and organize your files (or archive
                  files you don't need access to right now).
                </p>
                <FileBrowser
                  status="active"
                  initialFolderId={initialFolderId}
                  onFilesDeleted={handleFilesDeleted}
                  onFileArchived={handleFileArchived}
                  multiSelect={true}
                  className="min-h-[400px]"
                  refreshTrigger={activeRefreshTrigger}
                />
              </TabsContent>
              <TabsContent value="archived" forceMount className="data-[state=inactive]:hidden">
                <p className="text-sm text-muted-foreground mb-4">
                  Long-term storage at reduced cost. Restore files when you need them again.
                </p>
                <FileBrowser
                  status="archived"
                  onFilesDeleted={handleFilesDeleted}
                  onFileRestored={handleFileRestored}
                  multiSelect={true}
                  className="min-h-[400px]"
                  refreshTrigger={archivedRefreshTrigger}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MyDataPage;
